import React, { FC, MouseEvent } from 'react';

import { OnlyOneLeft } from 'components/OnlyOneLeft/OnlyOneLeft';
import { SelectedSize } from 'components/pages/ProductDetails/types/SelectedSize';
import { SelectorOption } from 'components/pages/ProductDetails/types/SelectorOption';
import { ProductInfoCard } from 'components/ProductInfoCard/ProductInfoCard';
import { SizeGuideActivator } from 'components/SizeGuide/SizeGuideActivator';
import { fontFamilies } from 'components/UI/Font';
import { getDisplaySizeSystemAlias } from 'helpers/sizeHelper';
import { Product } from 'models/product';

import { EyewearWarning } from '../EyewearWarning/EyewearWarning';

import { SizeSelector } from './SizeSelector/SizeSelector';

export const ProductActionCard: FC<{
  addToCart: (e: MouseEvent<HTMLElement>) => void;
  department: string;
  formattedModelNumber: string;
  gridArea: string;
  isLastOne: boolean;
  isOutOfStock: boolean;
  isUniversalSizeOnly: boolean;
  product: Product;
  selectedSize: SelectedSize;
  selectorOptions: SelectorOption[];
  selectorTitleText: string;
  setSelectedSize: (val: SelectedSize) => void;
  setSizeGuideVisible: (val: boolean) => void;
}> = ({
  addToCart,
  department,
  formattedModelNumber,
  gridArea,
  isLastOne,
  isOutOfStock,
  isUniversalSizeOnly,
  product,
  product: { sizeSystem, sizeSystemAlias },
  selectorOptions,
  selectedSize,
  selectorTitleText,
  setSelectedSize,
  setSizeGuideVisible,
}) => {
  const onAddToCartButton = e => {
    if (!selectedSize.selected) {
      setSelectedSize({
        selected: null,
        selectedOption: null,
        showSelectSizeBadge: true,
      });
    } else {
      addToCart(e);
    }
  };

  const displayedSizeSystemAlias = getDisplaySizeSystemAlias(sizeSystemAlias);
  return (
    <div className="product-action-card">
      <div className="product-info-card-wrapper">
        <ProductInfoCard isLastOne={isLastOne} product={product} />
      </div>

      <div className="actions">
        <div className="shipping-info desktop">Free express shipping worldwide</div>

        {isUniversalSizeOnly && isLastOne && (
          <div className="only-one-left-universal-size desktop-flex">
            <OnlyOneLeft lineHeight="20px" />
          </div>
        )}

        {!isUniversalSizeOnly && (
          <div className="over-selector">
            {/* You chose: {storeColor} {selectedSize.selectedOption ? selectedSize.selectedOption.label : ''}{' '} */}
            {isLastOne ? <OnlyOneLeft lineHeight="20px" /> : <div />}

            <div className="size-guide-activator-wrapper">
              <SizeGuideActivator
                department={department}
                margin="0"
                setSizeGuideVisible={() => {
                  setSizeGuideVisible(true);
                }}
              />
            </div>
          </div>
        )}

        {!isUniversalSizeOnly && (
          <SizeSelector
            isOutOfStock={isOutOfStock}
            selectedSize={selectedSize}
            selectorOptions={selectorOptions}
            selectorTitleText={selectorTitleText}
            setSelectedSize={setSelectedSize}
            sizeSystem={sizeSystem}
            sizeSystemAlias={displayedSizeSystemAlias}
          />
        )}

        <div className="button-add-to-cart" onClick={onAddToCartButton}>
          Add to bag
        </div>
        <EyewearWarning product={product} />

        <div className="shipping-info mobile">Free express shipping worldwide</div>

        {formattedModelNumber && <p className="model-number mobile">Model number: {formattedModelNumber}</p>}
      </div>

      <style jsx>{`
        .product-action-card {
          grid-area: ${gridArea};
          margin-top: 20px;
        }

        .product-info-card-wrapper,
        .actions {
          width: 100%;
        }

        .product-info-card-wrapper {
          margin-bottom: 31px;
        }

        .button-add-to-cart {
          background-color: black;
          border-radius: 5px;
          color: white;
          cursor: pointer;
          font-family: ${fontFamilies.bold};
          height: 44px;
          line-height: 44px;
          margin: 10px 0 10px 0;
          text-align: center;
        }
        .button-add-to-cart:hover {
          background-color: #222222;
        }

        @media (min-width: 1024px) {
          .product-info-card-wrapper {
            margin-bottom: 3px;
          }
          .actions {
            max-width: 300px;
          }
          .button-add-to-cart {
            margin-bottom: 5px;
          }
        }

        .over-selector {
          display: flex;
          justify-content: space-between;
          margin-bottom: 5px;
        }

        .only-one-left-universal-size {
          justify-content: flex-end;
          margin-bottom: 5px;
        }

        .size-guide-activator-wrapper {
          text-align: right;
        }
        @media (min-width: 1024px) {
          text-align: left;
        }

        .shipping-info {
          color: #f25c5e;
          text-transform: uppercase;
        }
        .shipping-info.desktop {
          margin-bottom: 44px;
        }
        .shipping-info.mobile {
          text-align: center;
          margin-bottom: 30px;
        }

        .mobile-number {
          margin-bottom: 30px;
        }
      `}</style>
    </div>
  );
};
